import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	EMBEDDED_CONFLUENCE_METRICS_PREFIX,
	EMBEDDED_VIEW_TTI,
	useEmbeddedConfluenceEventMarkers,
} from '@atlassian/embedded-confluence';
import { getIframeObservable_DO_NOT_USE } from '@atlassian/embedded-confluence/getIframeObservable';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { PageSegmentLoad } from '@confluence/browser-metrics/entry-points/pageSegmentLoadType.ts';
import type { PageLoad } from '@confluence/browser-metrics/entry-points/pageLoadType.ts';
import { ExperienceActionMeasures } from '@confluence/action-measures/entry-points/ExperienceActionMeasures.ts';
import {
	createPageLoadMetric,
	getWaterfallTimings,
	createPageSegmentLoadMetric,
} from '@confluence/browser-metrics/entry-points/metricConfigs.ts';
import {
	VIEW_EDIT_BUTTON_CLICKED_ACTION,
	VIEW_PAGE_LOAD_KEY,
	BYLINE_SEGMENT_KEY,
	APP_NAVIGATION_SEGMENT_KEY,
	CONTENT_TITLE_SEGMENT_KEY,
	SPACE_NAVIGATION_SEGMENT_KEY,
	PAGE_TREE_SEGMENT_KEY,
	FAVOURITE_BUTTON_SEGMENT_KEY,
	RESTRICTIONS_BUTTON_SEGMENT_KEY,
	WATCH_BUTTON_SEGMENT_KEY,
	VIEW_PAGE_CONTAINER_SEGMENT_KEY,
	OBJECT_HEADER_SEGMENT_KEY,
	OBJECT_SIDEBAR_CONTROL_SEGMENT_KEY,
} from '@confluence/browser-metrics/entry-points/metric-keys.ts';
import { resetViewTransitionSource } from '@confluence/browser-metrics/entry-points/viewTransitionSource.ts';
import { isInitial, markBrowserMetricEnd } from '@confluence/browser-metrics/entry-points/utils.ts';
import { resumeLowPriorityEvents } from '@confluence/analytics-event-delay';
import { APP_NAVIGATION_METRIC } from '@confluence/browser-metrics/entry-points/app-navigation.metric.ts';
import {
	BYLINE_METRIC,
	CONTENT_TITLE_METRIC,
} from '@confluence/content-header/entry-points/pageSegmentLoadMetrics.ts';
import { OBJECT_HEADER_SEGMENT_METRIC } from '@confluence/content-types-header/entry-points/pageSegmentLoadMetrics.ts';
import { FAVOURITE_BUTTON_METRIC } from '@confluence/favourite-button/entry-points/pageSegmentLoadMetrics.ts';
import { PAGE_TREE_METRIC } from '@confluence/page-tree/entry-points/pageSegmentLoadMetrics.ts';
import { SPACE_NAVIGATION_METRIC } from '@confluence/side-navigation/entry-points/pageSegmentLoadMetrics.ts';
import { markLegacyFY23TTI } from '@confluence/view-content-perf-metrics/entry-points/legacyFY23PerformanceMetrics.ts';
import { CONTENT_RENDERER_METRIC } from '@confluence/view-content-perf-metrics/entry-points/pageSegmentLoadMetrics.ts';
import { WATCH_BUTTON_METRIC } from '@confluence/watch-dialog/entry-points/pageSegmentLoadMetrics.ts';
import { OBJECT_SIDEBAR_CONTROL_METRIC } from '@confluence/object-sidebar-components/entry-points/pageSegmentLoadMetrics.ts';
import {
	debuggerEnabled,
	cacheExpectedViewPageSegments,
} from '@confluence/analytics-debugger/entry-points/AnalyticsDebugger.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import {
	getHydrationErrorWithRoute,
	getHydrationFailureOperationalEvent,
} from '@confluence/ssr-utilities/entry-points/matchHydrationError.ts';

/*
There isn't an explicit type for the page load segments passed to `expectedPageLoadSegments`.
However, we can leverage TypeScript's TypeOf Operator (https://www.typescriptlang.org/docs/handbook/2/typeof-types.html)
to create a type that represents page load segments.
*/

type PageLoadSegments = typeof CONTENT_RENDERER_METRIC;
export interface CustomViewPageLoadConfig {
	pageLoadKey?: PageLoad;
	fmpUntilStopTimeMetrics?: PageSegmentLoad[];
	expectedPageLoadSegments?: PageLoadSegments[];
}

const baseConfig = {
	slo: {
		// based on production p90
		fmp: {
			initial: { threshold: 14300 },
			transition: { threshold: 3200 },
		},
		tti: {
			initial: { threshold: 14300 },
			transition: { threshold: 3200 },
		},
	},
	histogram: {
		// based on pre-production pollinator and prod p90
		initial: {
			fmp: '5000_6000_7000_8000_10000_12000_14000',
			tti: '5000_6000_7000_8000_10000_12000_14000',
		},
		transition: {
			fmp: '2000_2500_3000_3500_4000_4500_5000',
			tti: '2000_2500_3000_3500_4000_4500_5000',
		},
	},
	timings: [
		// breakdowns in order of occurrence
		{ key: 'html', endMark: 'html' },
		{ key: 'fy21-legacy-fmp', endMark: 'fy21-legacy-fmp' },
		{ key: 'appLoad', startMark: 'html', endMark: 'app' },
		{
			key: 'appRootRender',
			startMark: 'app',
			endMark: 'root-component-render',
		},
		{
			key: 'routerMatched',
			startMark: 'root-component-render',
			endMark: 'route-manager-initialized',
		},
		{
			key: 'onBeforeAkRenderer',
			endMark: 'onBeforeAkRenderer',
		},
		{ key: 'fy21-legacy-tti', endMark: 'fy21-legacy-tti' },
		{ key: 'fy23-legacy-tti', endMark: 'fy23-legacy-tti' },
	],
};

const NOOP = () => {};

export const getExpectedPageLoadSegments = ({
	isLite,
	isAnonymous,
	isInitialLoad,
}: {
	isLite?: boolean;
	isAnonymous: boolean;
	isInitialLoad: boolean;
}) => {
	if (isLite) {
		return [BYLINE_METRIC, CONTENT_TITLE_METRIC, CONTENT_RENDERER_METRIC];
	}

	const segments = [
		// New metric adding to this list should also be added to fmpAsUntilStopTimeMetrics
		// See comments in fmpAsUntilStopTimeMetrics for more information
		BYLINE_METRIC,
		CONTENT_TITLE_METRIC,
		CONTENT_RENDERER_METRIC,
		VIEW_PAGE_CONTAINER_METRIC,
	];

	if (fg('confluence_frontend_content_wrapper')) {
		segments.push(OBJECT_SIDEBAR_CONTROL_METRIC);
	}

	if (fg('confluence_frontend_content_wrapper')) {
		segments.push(OBJECT_HEADER_SEGMENT_METRIC);
	}

	if (isInitialLoad) {
		segments.push(APP_NAVIGATION_METRIC, SPACE_NAVIGATION_METRIC);
		segments.push(PAGE_TREE_METRIC);
	}

	if (!isAnonymous) {
		// Note: the edit button metric is not currently tracked because there is no
		// guarantee that segment will fire even for authenticated users
		if (!fg('confluence_frontend_content_wrapper')) {
			segments.push(FAVOURITE_BUTTON_METRIC, WATCH_BUTTON_METRIC);
		}
	}

	return segments;
};

/**
 * fmpAsUntilStopTimeMetrics
 *
 * UPDATE 2025-04-29: TTI is deprecated. Going forward, new segments should be included here by default in order to
 * preserve the original TTI definition. Segments should be included even if they are not minimally interactive.
 *
 * What is minimally interactive?
 * Minimally interactive during the SSR phase at it's core, is determined if the component/element is clickable,
 * can register the action, and display a visual response that the action has been received.
 * The SSR phase can be stalled by appending ?NO_SPA=1 at the end of a CONNIE url.
 *
 * I.E. If you click on an inline comment/highlight, the click is registered, and displays a loading sidebar component.
 *
 * If a required segment is SSR'd and determined to be minimally interactive at FMP, ADD TO THIS LIST
 * If a required segment is SSR'd but determined to NOT be minimally interactive at FMP, DO NOT ADD TO THIS LIST
 * If a required segment is yet to be SSR'd, this config will not update TTI for that segment, by default is OFF.
 *
 * CONTEXT
 * the returned segments are considered to be minimally interactive during SSR
 * hence we want to use FMP (ssr-ttr mark)
 *
 * PageSegment TTI is measured: useFmpAsUntilStopTime ? FMP : Eventual load time
 * Content-renderer TTI: macros are SSRed ? FMP : Eventual Load Time
 *
 * View Page TTI is measured: Math.max(Non SSR PageSegments TTI, Content-renderer TTI)
 *
 */
const fmpAsUntilStopTimeMetrics = ({ isLite }): string[] => {
	if (isLite) {
		return [BYLINE_SEGMENT_KEY, CONTENT_TITLE_SEGMENT_KEY];
	}

	return [
		BYLINE_SEGMENT_KEY,
		CONTENT_TITLE_SEGMENT_KEY,
		APP_NAVIGATION_SEGMENT_KEY,
		SPACE_NAVIGATION_SEGMENT_KEY,
		PAGE_TREE_SEGMENT_KEY,
		FAVOURITE_BUTTON_SEGMENT_KEY,
		RESTRICTIONS_BUTTON_SEGMENT_KEY,
		WATCH_BUTTON_SEGMENT_KEY,
		VIEW_PAGE_CONTAINER_SEGMENT_KEY,
		OBJECT_HEADER_SEGMENT_KEY,
		OBJECT_SIDEBAR_CONTROL_SEGMENT_KEY,
	];
};

// This component assumes startBrowserMetricsPageLoad() has already been called from
// packages/confluence-frontend-server/src/components/Root/App.js in order to generate
// accurate pageLoad start timings. This function is supplementary and starts the
// `until` listeners which will automatically stop the pageLoad.

interface StartViewPageLoadParams {
	contentId: string;
	isLite?: boolean;
	customConfig?: CustomViewPageLoadConfig;
}

export const StartViewPageLoad = ({ contentId, isLite, customConfig }: StartViewPageLoadParams) => {
	const { userId } = useSessionData();
	const pageLoadMetricRef = useRef<ReturnType<typeof createPageLoadMetric>>();
	const sendMetrics = useEmbeddedConfluenceEventMarkers();
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	useEffect(() => {
		const _isInitialLoad = isInitial();
		const expectedPageSegments =
			customConfig?.expectedPageLoadSegments ??
			getExpectedPageLoadSegments({
				isAnonymous: Boolean(!userId),
				isInitialLoad: _isInitialLoad,
				isLite,
			});

		if (debuggerEnabled) {
			cacheExpectedViewPageSegments(expectedPageSegments);
		}

		// Marks a custom start time to track user clicks for temporary data gathering
		// Context: https://hello.atlassian.net/wiki/spaces/CCPERF/pages/1951426622
		ExperienceActionMeasures.markMeasureStart(VIEW_EDIT_BUTTON_CLICKED_ACTION);

		const untilExperienceMetricNames =
			customConfig?.fmpUntilStopTimeMetrics ??
			fmpAsUntilStopTimeMetrics({
				isLite,
			});

		// Create a list of untilPageSegments
		// This wraps the segments into an UntilExperience, so that @atlassian/browser-metrics can handle useFmpAsUntilStopTime gracefully
		// Note - this only affect initial load since transitions are not SSR'd, hence page-segments do not have fmp marks
		// More information why useFmpAsUntilStopTime can be found here
		// https://hello.atlassian.net/wiki/spaces/~484600559/pages/1515596554/Toolings+Update+Browser-Metrics+FMP+TTI
		const untilPageSegments = expectedPageSegments.map((segment) => ({
			experience: segment,
			useFmpAsUntilStopTime: untilExperienceMetricNames.includes(segment.key),
		}));

		pageLoadMetricRef.current = createPageLoadMetric({
			...baseConfig,
			key: customConfig?.pageLoadKey ?? VIEW_PAGE_LOAD_KEY,
			timings: [...baseConfig.timings, ...getWaterfallTimings(expectedPageSegments)],
			include: expectedPageSegments,
			until: untilPageSegments,
		});

		if (!_isInitialLoad) {
			// Make sure all segments are re-started and not currently marked as finished from a previous navigation
			expectedPageSegments.forEach((m) => m.startFromPageLoad());
		}

		pageLoadMetricRef.current.startPageLoad();
		pageLoadMetricRef.current.onStop(
			markBrowserMetricEnd.bind(null, pageLoadMetricRef.current),
			NOOP,
		);

		pageLoadMetricRef.current.onStop(resumeLowPriorityEvents, resumeLowPriorityEvents);
		pageLoadMetricRef.current.onStop(resetViewTransitionSource, resetViewTransitionSource);
		pageLoadMetricRef.current.onStop((data) => {
			markLegacyFY23TTI(pageLoadMetricRef.current, data);
		}, NOOP);
		pageLoadMetricRef.current.onStop(() => {
			if (isEmbeddedConfluence) {
				const observableObject = getIframeObservable_DO_NOT_USE();
				const uniqueKey = observableObject?.object?.uniqueKey;
				sendMetrics(
					{
						timestamp: Date.now(),
						customData: {
							...(uniqueKey && fg('platform_deprecate_lp_cc_embed') ? { uniqueKey } : {}),
						},
					},
					`${EMBEDDED_CONFLUENCE_METRICS_PREFIX}${EMBEDDED_VIEW_TTI}`,
				);
			}
		}, NOOP);
		pageLoadMetricRef.current.onStop(() => {
			if (_isInitialLoad && Boolean(window.__HYDRATION_FAILED__)) {
				const { error: errorWithRoute, route } = getHydrationErrorWithRoute(
					window.location.toString(),
				);

				if (expVal('confluence_frontend_hydration_failure_reporting', 'isEnabled', false)) {
					createAnalyticsEvent(
						getHydrationFailureOperationalEvent({
							source: 'viewPageCommon',
							route,
							errorWithRoute,
							isSSRReactStreaming: Boolean(window.__SSR_REACT_STREAMING__),
						}),
					).fire();
				} else {
					getMonitoringClient().submitError(errorWithRoute, {
						attribution: 'hydration',
					});
				}
			}
		}, NOOP);

		pageLoadMetricRef.current.onStop(() => {
			if (
				_isInitialLoad &&
				Boolean(window.__HYDRATION_FAILED__) &&
				Boolean(window.__SSR_REACT_STREAMING__) &&
				!expVal('confluence_frontend_hydration_failure_reporting', 'isEnabled', false)
			) {
				const { error: errorWithRoute } = getHydrationErrorWithRoute(window.location.toString());
				getMonitoringClient().submitError(errorWithRoute, {
					attribution: 'streaming-hydration',
				});
			}
		}, NOOP);

		// Cleanup on unmount or when dependencies have changed
		return () => {
			pageLoadMetricRef.current?.cancel();
		};
	}, [
		contentId,
		customConfig,
		userId,
		isLite,
		isEmbeddedConfluence,
		sendMetrics,
		createAnalyticsEvent,
	]);

	return null;
};

export const VIEW_PAGE_CONTAINER_METRIC = createPageSegmentLoadMetric({
	key: VIEW_PAGE_CONTAINER_SEGMENT_KEY,
	ssr: Boolean(window.__SSR_RENDERED__) ? { doneAsFmp: true } : undefined,
});
